import { FC, memo, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Box,
  Skeleton,
  Typography,
  CircularProgress,
} from '@mui/material';
import InboxIcon from '@mui/icons-material/Inbox';
import { IImportPropertiesStepProps, SyncStatus } from '../../types';
import { CHANNEL_INFO, T_KEYS } from '../../constants';
import {
  ImportHeader,
  ImportTitle,
  ImportLaterButton,
  PropertiesCard,
  PropertiesHeader,
  PropertiesCount,
  PropertyImage,
  PropertyName,
  PropertyAddress,
  PropertyId,
  PropertyStatus,
  ImportFooter,
  PrimaryButton,
} from '../../styles';

// Import channel SVGs
import AIRBNB from '@assets/svg/airbnb.svg?react';
import GATHERN from '@assets/svg/gathren.svg?react';
import BookingIcon from '@assets/svg/BookingLogo.svg?react';

// Placeholder component for properties without images
const PlaceholderImage = () => (
  <div style={{ 
    width: '100%', 
    height: '100%', 
    background: 'linear-gradient(135deg, #c4b5fd 0%, #a78bfa 100%)',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }}>
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M8.5 10C9.32843 10 10 9.32843 10 8.5C10 7.67157 9.32843 7 8.5 7C7.67157 7 7 7.67157 7 8.5C7 9.32843 7.67157 10 8.5 10Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M21 15L16 10L5 21" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  </div>
);

const ImportPropertiesStep: FC<IImportPropertiesStepProps> = memo(({
  channelName,
  properties,
  onStartImport,
  onImportLater,
  isLoading,
  importingIds = [],
  isImportButtonLoading = false,
}) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const channelInfo = CHANNEL_INFO[channelName];
  const displayName = isRtl ? channelInfo.displayNameAr : channelInfo.displayName;

  // Get channel logo component
  const getChannelLogo = () => {
    switch (channelName) {
      case 'airbnb':
        return <AIRBNB style={{ width: 24, height: 24 }} />;
      case 'booking':
        return <BookingIcon />;
      case 'gathern':
        return <GATHERN style={{ width: 24, height: 24 }} />;
      default:
        return null;
    }
  };

  // Handle checkbox toggle
  const handleToggle = useCallback((propertyId: string) => {
    setSelectedIds((prev) =>
      prev.includes(propertyId)
        ? prev.filter((id) => id !== propertyId)
        : [...prev, propertyId]
    );
  }, []);

  // Handle select all
  const handleSelectAll = useCallback(() => {
    const availableIds = properties
      .filter((p) => !p.isImported)
      .map((p) => p.id);
    
    if (selectedIds.length === availableIds.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(availableIds);
    }
  }, [properties, selectedIds]);

  // Handle import
  const handleImport = useCallback(() => {
    if (selectedIds.length > 0) {
      onStartImport(selectedIds);
    }
  }, [selectedIds, onStartImport]);

  const availableCount = properties.filter((p) => !p.isImported).length;

  // Clear selected IDs when properties become imported
  useEffect(() => {
    const importedIds = properties.filter((p) => p.isImported).map((p) => p.id);
    setSelectedIds((prev) => prev.filter((id) => !importedIds.includes(id)));
  }, [properties]);

  return (
    <>
      {/* Info text row - white card matching Figma (node 1:11241) */}
      {/* Only show header when there are units not imported */}
      {availableCount > 0 && (
        <ImportHeader $isRtl={isRtl}>
          <ImportTitle>
            {t(T_KEYS.FOUND_UNITS_DESC, { count: properties.length, channel: displayName })}
          </ImportTitle>
          <ImportLaterButton onClick={onImportLater}>
            {t(T_KEYS.IMPORT_LATER)}
          </ImportLaterButton>
        </ImportHeader>
      )}

      <PropertiesCard>
        {/* Properties Header - INSIDE the table card (node 1:11246) */}
        <PropertiesHeader $isRtl={isRtl}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontWeight: 600, color: '#000', fontSize: '14px' }}>
              {t(T_KEYS.CHANNEL_UNITS, { channel: displayName })}
            </span>
            {getChannelLogo()}
          </div>
          <PropertiesCount>
            {t(T_KEYS.UNITS_COUNT_LABEL, { count: properties.length })}
          </PropertiesCount>
        </PropertiesHeader>
        {/* MUI Table - Matching Figma node 1:11396 exactly */}
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { py: '4px', px: '8px' } }}>
                <TableCell 
                  padding="checkbox" 
                  sx={{ 
                    borderBottom: '1px solid #E9E9E7', 
                    bgcolor: 'transparent',
                    p: '4px !important',
                  }}
                >
                  <Checkbox
                    checked={selectedIds.length === availableCount && availableCount > 0}
                    indeterminate={selectedIds.length > 0 && selectedIds.length < availableCount}
                    onChange={handleSelectAll}
                    size="small"
                    sx={{
                      color: '#d0d5dd',
                      borderRadius: '6px',
                      '&.Mui-checked': { color: '#5b3fff' },
                      '&.MuiCheckbox-indeterminate': { color: '#5b3fff' },
                    }}
                  />
                </TableCell>
                <TableCell sx={{ 
                  color: '#37352F', 
                  fontWeight: 700, 
                  fontSize: '12px', 
                  borderBottom: '1px solid #E9E9E7', 
                  bgcolor: 'transparent',
                  textAlign: 'start'
                }}>
                  {t(T_KEYS.TABLE_PROPERTY)}
                </TableCell>
                <TableCell sx={{ 
                  color: '#37352F', 
                  fontWeight: 700, 
                  fontSize: '12px', 
                  borderBottom: '1px solid #E9E9E7', 
                  bgcolor: 'transparent',
                  textAlign: 'start'
                }}>
                  {t(T_KEYS.TABLE_ADDRESS)}
                </TableCell>
                <TableCell sx={{ 
                  color: '#37352F', 
                  fontWeight: 700, 
                  fontSize: '12px', 
                  borderBottom: '1px solid #E9E9E7', 
                  bgcolor: 'transparent',
                  textAlign: 'start'
                }}>
                  {t(T_KEYS.TABLE_ID)}
                </TableCell>
                <TableCell sx={{ 
                  color: '#37352F', 
                  fontWeight: 700, 
                  fontSize: '12px', 
                  borderBottom: '1px solid #E9E9E7', 
                  bgcolor: 'transparent',
                  textAlign: 'start'
                }}>
                  {t(T_KEYS.TABLE_STATUS)}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Loading skeleton state */}
              {isLoading && (
                <>
                  {[1, 2, 3, 4].map((index) => (
                    <TableRow key={`skeleton-${index}`} sx={{ height: '60px', '& td': { borderBottom: '1px solid #E9E9E7', py: '8px' } }}>
                      <TableCell padding="checkbox">
                        <Skeleton variant="rectangular" width={20} height={20} sx={{ borderRadius: '4px' }} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <Skeleton variant="rectangular" width={48} height={48} sx={{ borderRadius: '8px' }} />
                          <Skeleton variant="text" width={150} height={24} />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={200} height={20} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={100} height={20} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: '12px' }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              )}

              {/* Empty state */}
              {!isLoading && properties.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ border: 'none', py: 8 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      gap: 2,
                      py: 4,
                    }}>
                      <InboxIcon sx={{ fontSize: 64, color: '#d0d5dd' }} />
                      <Typography variant="h6" color="text.secondary" fontWeight={500}>
                        {t(T_KEYS.NO_UNITS_AVAILABLE)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t(T_KEYS.NO_UNITS_AVAILABLE_DESC)}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}

              {/* Data rows */}
              {!isLoading && properties.map((property) => (
                <TableRow
                  key={property.id}
                  hover
                  onClick={() => !property.isImported && handleToggle(property.id)}
                  sx={{
                    cursor: property.isImported ? 'default' : 'pointer',
                    opacity: property.isImported ? 0.6 : 1,
                    bgcolor: selectedIds.includes(property.id) ? '#f8f7ff' : 'transparent',
                    '&:hover': { bgcolor: '#f8f7ff' },
                    height: '60px',
                    '& td': { 
                      borderBottom: '1px solid #E9E9E7',
                      py: '8px',
                      textAlign: 'start',
                    },
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.includes(property.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleToggle(property.id);
                      }}
                      disabled={property.isImported}
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                      sx={{
                        color: '#d0d5dd',
                        borderRadius: '6px',
                        '&.Mui-checked': { color: '#5b3fff' },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <PropertyImage>
                        {property.imageUrl ? (
                          <img 
                            src={property.imageUrl} 
                            alt={property.name}
                            onError={(e) => {
                              // Hide the broken image and show placeholder
                              (e.target as HTMLImageElement).style.display = 'none';
                              (e.target as HTMLImageElement).parentElement?.classList.add('show-placeholder');
                            }}
                            style={{ 
                              width: '100%', 
                              height: '100%', 
                              objectFit: 'cover',
                              borderRadius: '4px',
                            }}
                          />
                        ) : (
                          <PlaceholderImage />
                        )}
                      </PropertyImage>
                      <PropertyName>{property.name || '-'}</PropertyName>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <PropertyAddress>
                      {property.location || '-'}
                    </PropertyAddress>
                  </TableCell>
                  <TableCell>
                    <PropertyId>{property.externalId || '-'}</PropertyId>
                  </TableCell>
                  <TableCell>
                    {importingIds.includes(property.id) ? (
                      <PropertyStatus $syncStatus={SyncStatus.InProgress} $importing={true}>
                        <span>{t(T_KEYS.IMPORTING)}</span>
                        <CircularProgress size={14} sx={{ color: '#D97706' }} />
                      </PropertyStatus>
                    ) : (
                      <PropertyStatus $syncStatus={property.syncStatus ?? SyncStatus.NotSynced}>
                        {isRtl 
                          ? (property.syncStatusNameAr || t(T_KEYS.NOT_IMPORTED))
                          : (property.syncStatusName || t(T_KEYS.NOT_IMPORTED))}
                      </PropertyStatus>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

      </PropertiesCard>
        <ImportFooter>
          <PrimaryButton
            $disabled={selectedIds.length === 0 || isLoading || isImportButtonLoading || importingIds.length > 0}
            onClick={handleImport}
            disabled={selectedIds.length === 0 || isLoading || isImportButtonLoading || importingIds.length > 0}
          >
            {(isImportButtonLoading || importingIds.length > 0) ? (
              <>
                <CircularProgress size={16} sx={{ color: 'white', marginInlineEnd: '8px' }} />
                {t(T_KEYS.IMPORTING_LOADING)}
              </>
            ) : isLoading ? (
              t(T_KEYS.IMPORTING)
            ) : (
              `${t(T_KEYS.START_IMPORT)} ${selectedIds.length > 0 ? `(${selectedIds.length})` : ''}`
            )}
          </PrimaryButton>
        </ImportFooter>
    </>
  );
});

ImportPropertiesStep.displayName = 'ImportPropertiesStep';

export default ImportPropertiesStep;
